import { FetchError } from 'ofetch';
import { type FC, useEffect, useRef } from 'react';
import type { FormState, UseFormSetError } from 'react-hook-form';
import { Banner } from './Banner';
import { cn } from './react';

export const setFormErrors =
  (setError: UseFormSetError<never>) =>
  (e?: unknown): string[] => {
    const messages: string[] = [];
    if (!(e instanceof FetchError)) {
      messages.push(e instanceof Error ? e.message : `Unhandled Error`);
    } else {
      const data = typeof e.data === 'object' && e.data ? e.data : {};
      Object.entries(data).forEach(([key, val]) => {
        const errKey =
          key === 'non_field_errors' || key === 'detail' ? 'root' : key;
        const errMsg =
          typeof val === 'string' ? [val] : Array.isArray(val) ? val : [];
        if (errMsg.length && errKey) {
          messages.push(...(errMsg as string[]));
        }
      });
      if (messages.length === 0) {
        messages.push(
          e.message ||
            e.statusMessage ||
            e.statusText ||
            e.status?.toString() ||
            e.statusCode?.toString() ||
            'Unknown Network Error',
        );
      }
    }
    setError('root', {
      message: messages.join('\n'),
    });
    return messages;
  };

export const FormErrors: FC<{
  className?: string;
  errors?: FormState<any>['errors'] | undefined;
}> = ({ errors: formErrors, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const errors = Object.values(formErrors ?? {})
    .flatMap((x) => x?.message?.toString().split('\n'))
    .filter((x) => !!x) as string[];
  useEffect(() => {
    if (errors.length > 0 && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const partiallyVisible = rect.top < window.innerHeight && rect.bottom > 0;
      if (partiallyVisible) return;
      ref.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [errors.length]);
  if (errors.length === 0) return null;
  return (
    <div
      className={cn('whitespace-pre-line space-y-2 w-full', className)}
      ref={ref}
    >
      {errors.map((msg, i) => (
        <Banner
          key={`${msg}-${i.toString()}`}
          variant="negative"
          className="w-full"
          size={10}
        >
          {msg}
        </Banner>
      ))}
    </div>
  );
};
